"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sun, CloudRain, Snowflake, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const seasonalityPhases = [{
  phase: "Summer Peak",
  timing: "April – June",
  demand: "High Surge (85–98%)",
  iconName: "Sun",
  strategy: "Premium dynamic ADR fencing, multi-night minimum stay (2-3 nights), strict cancellation rules."
}, {
  phase: "Monsoon Shoulder",
  timing: "July – August",
  demand: "Low / Off-Peak (35–50%)",
  iconName: "CloudRain",
  strategy: "Drive staycation packages, workation inclusions, OTA opaque discounting, and corporate/MICE capture."
}, {
  phase: "Autumn & Festive",
  timing: "September – November",
  demand: "Steady Leisure (65–80%)",
  iconName: "Sparkles",
  strategy: "Target Delhi-NCR long-weekend drive-in traffic, direct channel value adds, and OTA visibility boost."
}, {
  phase: "Winter & Snow Surge",
  timing: "December – February",
  demand: "Peak Spikes (80–95%)",
  iconName: "Snowflake",
  strategy: "Real-time weather/snowfall rate spikes, Christmas/New Year mandatory gala packages, yield capping."
}];
const challenges = [{
  id: 1,
  title: "Seasonality & Weekend Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage sharp 48-hour Delhi-NCR weekend surges against low weekday occupancy and monsoon dips. We engineer dynamic minimum length-of-stay controls and pace-based pricing so your hill retreat never relies on distress discounting.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based dynamic pacing & weekend-to-weekday stay-through restrictions"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate shifts across Mall Road, Picture Palace, Library Chowk, and Barlowganj properties on MakeMyTrip, Agoda, and Booking.com. Automated competitive alerts protect your RevPAR while securing high-yield leisure bookings.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "The OTA Commission & Parity Trap",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Stop unauthorized B2B wholesaler leakage and reclaim margins lost to steep OTA commission cuts. We enforce strict rate parity across Google Hotels and drive direct website conversions through targeted value-add booking perks.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct booking engine conversion boost"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapemussoorie" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-14 md:mb-18">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Mussoorie
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Mussoorie operates on sharp weekend compression, high seasonal demand swings between blistering summer peaks and monsoon troughs, and fierce micro-market competition between Mall Road boutique hotels and secluded mountain resorts. Sunil Singh structures disciplined, algorithm-led revenue frameworks to maximize RevPAR across every quarter.
          </p>
        </div>

        {/* Seasonality Snapshot Block */}
        <div className="mb-14 md:mb-18 rounded-lg border border-border bg-card p-6 md:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-border/70">
            <div>
              <span className="text-xs font-mono font-semibold uppercase tracking-wider text-secondary">
                Market Intelligence
              </span>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground mt-1">
                Mussoorie Demand & Seasonality Snapshot
              </h3>
            </div>
            <Badge variant="secondary" className="w-fit">
              Annual Yield Optimization Model
            </Badge>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {seasonalityPhases.map((phase, i) => <div key={phase.phase} data-index={i} className="flex flex-col justify-between rounded-md border border-border/80 bg-muted/30 p-4 transition-colors hover:border-secondary/50">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2.5">
                    <div className="inline-flex size-8 items-center justify-center rounded-md bg-secondary/15 text-secondary-foreground">
                      {phase.iconName === "Sun" && <Sun className="size-4 text-chart-2" />}
                      {phase.iconName === "CloudRain" && <CloudRain className="size-4 text-chart-2" />}
                      {phase.iconName === "Sparkles" && <Sparkles className="size-4 text-chart-2" />}
                      {phase.iconName === "Snowflake" && <Snowflake className="size-4 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono text-muted-foreground">
                      {phase.timing}
                    </span>
                  </div>
                  <h4 className="text-sm font-bold text-foreground mb-1">
                    {phase.phase}
                  </h4>
                  <div className="text-xs font-medium text-secondary mb-2">
                    {phase.demand}
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {phase.strategy}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 rounded-xl">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-xl bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Mussoorie hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}